// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Browser-side key derivation for the vault encryption system.
 *
 * Uses Argon2id (via hash-wasm) to derive a 32-byte AES-256 key from
 * the user's password (or vault passphrase) and their UUID as salt.
 *
 * The derived key is sent to `POST /api/v1/vault/unlock` so the backend
 * can store it in Redis (encrypted with the server master key) for
 * encrypting/decrypting cloud-synced data blobs.
 *
 * Argon2id parameters are chosen for browser feasibility:
 *   - memory: 64 MiB (65536 KiB)
 *   - iterations: 3
 *   - parallelism: 1 (WASM is single-threaded)
 *   - hash length: 32 bytes (AES-256 key)
 */

import { argon2id } from 'hash-wasm';

/** Argon2id parameters — tuned for browser WASM performance. */
const ARGON2_MEMORY_COST = 65536; // 64 MiB in KiB
const ARGON2_TIME_COST = 3;
const ARGON2_PARALLELISM = 1;
const ARGON2_HASH_LENGTH = 32; // AES-256 key size

/**
 * Derive a 32-byte vault decryption key from a password and user UUID.
 *
 * @param secret - The user's password or vault passphrase (plaintext).
 * @param userUUID - The user's UUID string, used as Argon2id salt.
 * @returns A Uint8Array of 32 bytes suitable for AES-256-GCM.
 */
export async function deriveVaultKey(
  secret: string,
  userUUID: string,
): Promise<Uint8Array> {
  // Encode the UUID as UTF-8 bytes for the salt. Argon2id requires
  // a salt of at least 8 bytes — a UUID string is 36 bytes.
  const salt = new TextEncoder().encode(userUUID);

  const hashHex = await argon2id({
    password: secret,
    salt,
    parallelism: ARGON2_PARALLELISM,
    iterations: ARGON2_TIME_COST,
    memorySize: ARGON2_MEMORY_COST,
    hashLength: ARGON2_HASH_LENGTH,
    outputType: 'hex',
  });

  // Convert hex string to Uint8Array.
  return hexToBytes(hashHex);
}

/**
 * Encode a Uint8Array as a base64 string for JSON transport.
 * Go's `encoding/json` decodes base64 strings into `[]byte` fields.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/** Convert a hex string to Uint8Array. */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}
